import type { EntityVisitedEvent } from '@bridge/reactEvents';

export interface HistoryEntry extends EntityVisitedEvent {
  to: string;
  navobj: Record<string, number>;
}

export const historyStorageKey = 'texas_entity_history';
export const historyScopeLimit = 20;
export const historyTotalLimit = 200;

export function recordEntityVisit(history: unknown, event: EntityVisitedEvent) {
  return normalizeHistory([event, ...(Array.isArray(history) ? history : [])]);
}

export function selectEntityHistory(
  history: HistoryEntry[],
  customerId: number | null | undefined,
  betaFeatures: boolean,
) {
  return history
    .filter(
      (entry) =>
        (!customerId || entry.customerId === customerId) &&
        (betaFeatures || (entry.type !== 'inquiry' && entry.type !== 'offer')),
    )
    .slice(0, historyScopeLimit);
}

export function normalizeHistory(value: unknown): HistoryEntry[] {
  if (!Array.isArray(value)) return [];

  const entries: HistoryEntry[] = [];
  const seen = new Set<string>();
  const scopeCounts = new Map<number | undefined, number>();

  for (const candidate of value) {
    const entry = toHistoryEntry(candidate);
    if (!entry) continue;
    const key = `${entry.type}-${entry.id}`;
    const scopeCount = scopeCounts.get(entry.customerId) ?? 0;
    if (seen.has(key) || scopeCount >= historyScopeLimit) continue;

    entries.push(entry);
    seen.add(key);
    scopeCounts.set(entry.customerId, scopeCount + 1);
    if (entries.length === historyTotalLimit) break;
  }

  return entries;
}

function toHistoryEntry(
  event: EntityVisitedEvent | null | undefined,
): HistoryEntry | null {
  if (!event || typeof event.id !== 'number' || typeof event.label !== 'string')
    return null;

  const customerId = event.type === 'customer' ? event.id : event.customerId;
  const base = {
    id: event.id,
    label: event.label,
    type: event.type,
    customerId,
  };

  switch (event.type) {
    case 'article':
    case 'customer':
    case 'concept':
    case 'supplier':
      return {
        ...base,
        customerId: event.type === 'supplier' ? undefined : customerId,
        to: event.type,
        navobj: { [`${event.type}Id`]: event.id },
      };
    case 'inquiry':
    case 'offer':
      return {
        ...base,
        to:
          event.type === 'inquiry'
            ? 'variant.inquiry.detail'
            : 'variant.offers.group',
        navobj: {
          ...event.navobj,
          [event.type === 'inquiry' ? 'inquiryId' : 'offerGroupId']: event.id,
        },
      };
    default:
      return null;
  }
}
